import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, TableSortable, Typography } from '@saviynt/design-system';

import { CF_SEC_BLD } from '../../../shared/constants';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import type { ReportActiveColumnTypes, RootState } from '../../../shared/types';
import {
  DETAIL_PRIORITY_COLUMNS,
  PERMISSIONS_PRIORITY_COLUMNS,
  sortColumnsByPriority,
  sortOptions,
  SUMMARY_PRIORITY_COLUMNS,
} from '../data-layer/violationsReportConstants';
import { type SodViolationReportData } from '../data-layer/violationsReportTypes';
import { type ReportTableProps } from '../types';

import ExportBanner from './ExportBanner';

/**
 * ReportTable
 */
function ReportTable({ onColumnConfigChange }: ReportTableProps): ReactElement {
  const l = useInvertedLocalize();

  const localizedSortOptions = useMemo(
    () =>
      sortOptions.map((opt) => ({
        ...opt,
        label: l(opt.label),
      })),
    [l]
  );

  const [columns, setColumns] = useState<
    {
      key: string;
      label: string;
      value: string;
      isSortable: boolean;
      isChecked: boolean;
      isPinned: boolean;
      isDisabled: boolean;
      width: string;
      isColumnConfigEnabled?: boolean;
    }[]
  >([]);

  const [row, setRow] = useState<SodViolationReportData[]>([]);
  const isUpdatingFromActiveColumns = useRef(false);

  const { activeColumns, reportData, selectedReport } = useTypedSelector(
    (state: RootState) => state.aag.report
  ) as {
    activeColumns: ReportActiveColumnTypes[];
    reportData: SodViolationReportData[];
    selectedReport: string;
  };

  // Helper function to generate column configuration for export
  const generateColumnConfig = useCallback(
    (columnsToProcess: typeof columns) => {
      if (columnsToProcess.length > 0 && onColumnConfigChange) {
        const selectedColumns = columnsToProcess
          .filter((col) => col.isChecked && col.key !== 'actionColumn')
          .map((col) => col.value);

        const columnOrder = columnsToProcess
          .filter((col) => col.isChecked && col.key !== 'actionColumn')
          .reduce<Record<string, number>>((order, col, index) => {
            order[col.value] = index + 1;

            return order;
          }, {});

        onColumnConfigChange({
          selectedColumns,
          columnOrder,
        });
      }
    },
    [onColumnConfigChange]
  );

  // Handle activeColumns changes from Redux state
  useEffect(() => {
    if (activeColumns.length) {
      isUpdatingFromActiveColumns.current = true;

      // Get priority columns based on selected report type
      let priorityColumns = null;

      if (selectedReport === 'summary') {
        priorityColumns = SUMMARY_PRIORITY_COLUMNS;
      } else if (selectedReport === 'detail') {
        priorityColumns = DETAIL_PRIORITY_COLUMNS;
      } else if (selectedReport === 'permissions') {
        priorityColumns = PERMISSIONS_PRIORITY_COLUMNS;
      }

      // Sort columns by priority if available
      const sortedColumns = priorityColumns
        ? sortColumnsByPriority(activeColumns, priorityColumns)
        : activeColumns;

      const modifiedColumns = sortedColumns.map((column) => {
        // Get proper label from priority columns if available
        const priorityColumn = priorityColumns?.find(
          (p) => p.value === column.value
        );
        const displayLabel = priorityColumn
          ? l(priorityColumn.label)
          : column.label;

        return {
          key: column.value,
          label: displayLabel,
          value: column.value,
          isSortable: true,
          isChecked: column.isChecked,
          isDisabled: false,
          isPinned: column.value === 'riskName',
          width:
            column.value === 'riskName' || column.value === 'riskDescription'
              ? '20rem'
              : '12.25rem',
          isColumnConfigEnabled: false,
        };
      });

      modifiedColumns.push({
        key: 'actionColumn',
        label: 'actionColumn',
        value: 'actionColumn',
        isSortable: false,
        isPinned: true,
        isColumnConfigEnabled: true,
        isDisabled: true,
        isChecked: true,
        width: '3rem',
      });

      setColumns(modifiedColumns);

      // Generate column configuration for export
      generateColumnConfig(modifiedColumns);

      // Reset the flag after state update
      setTimeout(() => {
        isUpdatingFromActiveColumns.current = false;
      }, 0);
    }
  }, [activeColumns, selectedReport, generateColumnConfig, l]);

  // Handle column configuration changes when user modifies columns (separate effect)
  useEffect(() => {
    // Only run if we're not currently updating from activeColumns
    if (columns.length > 0 && !isUpdatingFromActiveColumns.current) {
      generateColumnConfig(columns);
    }
  }, [columns, generateColumnConfig]);

  useEffect(() => {
    if (Array.isArray(reportData)) {
      if (reportData.length > 0) {
        const modifiedReportData = reportData.map((item) => {
          const result: Record<string, React.ReactElement> = {};

          Object.entries(item).forEach(([key, value]) => {
            // Convert array values to string with newlines
            let displayValue = Array.isArray(value)
              ? value.join('\n')
              : String(value);

            // Transform Risk_Accepted to Mitigated for violation status
            if (
              key.toLowerCase().includes('violationstatus') &&
              displayValue === 'RISK_ACCEPTED'
            ) {
              displayValue = 'MITIGATED';
            }

            result[key] = (
              <Typography kind='body2' color={CF_SEC_BLD} lineLimit={2}>
                {displayValue}
              </Typography>
            );
          });

          return result;
        });

        setRow(modifiedReportData);
      } else {
        setRow([]);
      }
    }
  }, [reportData]);

  if (typeof reportData === 'string') {
    return (
      <Box
        tag='div'
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        {l('No Record Found. Change some filters and try again.')}
      </Box>
    );
  }

  if (row.length === 0) {
    return <Box />;
  }

  const sortOptionsConfig = columns
    .filter((item) => item.key && item.isSortable)
    .map((item) => ({ key: item.key, sortOptions: localizedSortOptions }));

  return (
    <Box className='Sod-Vio-Report-table ReportTable'>
      <ExportBanner className='ReportTable-exportBanner' />
      <Typography
        kind='body2'
        color={CF_SEC_BLD}
        desktop='body2-d'
        mobile='body2-d'>
        {row.length} {row.length === 1 ? l('Item') : l('Items')}
      </Typography>
      <TableSortable
        data={row}
        columns={columns}
        setColumns={setColumns}
        sortConfigs={sortOptionsConfig}
        isWindowScroll={false}
        isVirtualizationEnabled
        tableHeight='calc(100vh - 16rem)'
        columnConfigs={{
          isDndDisabled: false,
          isHeaderSticky: true,
          isHorizontalScroll: true,
        }}
      />
    </Box>
  );
}

ReportTable.defaultProps = {
  onColumnConfigChange: undefined,
};

export default memo(ReportTable);
